import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert, Box, Button, Card, Checkbox, Collapse, Dialog, DialogActions, DialogContent,
  DialogTitle, FormControlLabel, IconButton, MenuItem, Snackbar,
  Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import { alpha, Theme } from '@mui/material/styles';
import {
  UploadFileOutlined as UploadIcon,
  DeleteOutline as DeleteIcon,
  OpenInNewOutlined as OpenIcon,
  PhoneInTalkOutlined as CallIcon,
  ChevronLeftOutlined as PrevIcon,
  ChevronRightOutlined as NextIcon,
  ChatBubbleOutlineOutlined as MessageIcon,
  FileDownloadOutlined as DownloadIcon,
  HowToRegOutlined as HireIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  BlockOutlined as CloseIcon,
  ReplayOutlined as ReopenIcon,
  DescriptionOutlined as DocIcon,
  PhoneOutlined as PhoneIcon,
  MailOutlined as MailIcon,
  CheckCircleOutline as CopiedIcon,
  CheckCircle as DoneIcon,
  ExpandMoreOutlined as ExpandIcon,
  EditOutlined as EditIcon,
} from '@mui/icons-material';
import HiringService, {
  CandidateDetail as Detail, Checkpoint, CheckpointStatus, HireResult, CandidateOutcome,
  AuditEntry, CandidateStage, CandidateSource,
} from '../../api/services/hiring.service';
import PageBody from '../../components/ui/PageBody';
import PageHeader from '../../components/ui/PageHeader';
import MessageComposer from './MessageComposer';
import InterviewMode from './InterviewMode';
import { LoadingState } from '../../components/ui/States';
import { usePageBreadcrumb } from '../../components/layout/Breadcrumb';
import { useAuthStore } from '../../store/authStore';
import { status as statusTokens } from '../../theme';
import HiringPanel from './HiringPanel';
import { usePipelineOrder } from './pipelineCache';
import HireDialog from './HireDialog';
import CheckpointList from './CheckpointList';
import UploadCheckpointDialog, { needsExpiry } from './UploadCheckpointDialog';
import Confetti from './Confetti';
import { refreshStalled } from './useStalled';
import {
  STAGE_LABEL, OUTCOME_LABEL, SOURCE_LABEL, CLOSE_OUTCOMES, nextAction, moveFor,
  MOVE_LABEL, OutcomeChip, MoveDot, checkpointLabel, candidateName, formatDate, moveColor,
  stageLabel, nextTone, dueWords, todoSentence, withWhom,
} from './hiringUI';

// One candidate.
//
// This replaces the old tracker's slide-out drawer, which had to be a
// drawer because the grid it slid over was the only screen. A page can
// hold the checkpoint list, the files, and the history of what everybody
// did — the last of which the spreadsheet had no way to keep at all.
//
// The first version of this page put all eighteen checkpoints, eleven form
// fields and the files in one column and the history beside them, so the
// column you had to scroll was the one carrying the work. Details and
// files are reference; they sit on the right. The checklist and what
// happened sit on the left, where the reading starts.

/** Puts a value on the clipboard and says so, briefly. */
const CopyButton: React.FC<{ icon: React.ReactNode; value: string }> = ({ icon, value }) => {
  const [copied, setCopied] = useState(false);
  return (
    <Tooltip title={copied ? 'Copied' : `Copy ${value}`}>
      <Button
        size="small"
        variant="outlined"
        startIcon={copied ? <CopiedIcon /> : icon}
        onClick={() => {
          navigator.clipboard.writeText(value);
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        }}
        sx={{ maxWidth: 320, '& .MuiButton-startIcon': { flexShrink: 0 } }}
      >
        <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{value}</Box>
      </Button>
    </Tooltip>
  );
};

/** A labelled group of fields, so related ones sit together. */
const FieldGroup: React.FC<{
  title: string;
  /** [filled, total] — counted on the page, from the saved record. */
  progress?: [number, number];
  children: React.ReactNode;
}> = ({ title, progress, children }) => {
  // A group says how far along it is, so a glance down the column shows
  // which box still wants something: "3 / 5" while it is short, and a
  // green tick and a faint green wash once every box in it is filled.
  const complete = !!progress && progress[1] > 0 && progress[0] >= progress[1];
  return (
    <Box
      sx={{
        bgcolor: complete ? 'success.light' : 'action.hover',
        transition: 'background-color .3s ease',
        borderRadius: 2, p: 2.25,
        // Reference material, set a size down — fourteen inputs at body
        // size read as a form demanding to be filled, and this is a
        // record to consult.
        '& .MuiInputBase-input': { fontSize: 13 },
        '& .MuiInputBase-root': { fontSize: 13 },
        '& .MuiInputLabel-root': { fontSize: 12.5 },
        '& .MuiFormControlLabel-label': { fontSize: 13 },
      }}
    >
      <Stack direction="row" alignItems="center" gap={0.75} sx={{ mb: 1.75 }}>
        <Typography
          variant="caption"
          sx={{
            color: complete ? 'success.dark' : 'text.secondary', fontWeight: 700,
            fontSize: 10.5, letterSpacing: '.07em', textTransform: 'uppercase',
          }}
        >
          {title}
        </Typography>
        {complete ? (
          <Tooltip title="Every field is filled">
            <DoneIcon sx={{ fontSize: 15, color: 'success.main' }} />
          </Tooltip>
        ) : progress && progress[1] > 0 ? (
          <Typography
            variant="caption"
            sx={{ ml: 'auto', color: 'text.disabled', fontSize: 11, fontVariantNumeric: 'tabular-nums' }}
          >
            {progress[0]} / {progress[1]}
          </Typography>
        ) : null}
      </Stack>
      {/* 20px between fields, not 14. At the tighter setting the boxes read
          as one block of outlines rather than as separate things being
          recorded, which is the complaint this is answering. */}
      <Stack gap={2.5}>{children}</Stack>
    </Box>
  );
};

/** How many of a group's values are filled in, as FieldGroup's [filled, total]. */
const countFilled = (values: unknown[]): [number, number] => [
  values.filter((v) => v !== undefined && v !== null && String(v).trim() !== '').length,
  values.length,
];

const CandidateDetailPage: React.FC = () => {
  const { candidateId } = useParams<{ candidateId: string }>();
  const navigate = useNavigate();
  const { canArea } = useAuthStore();

  const [detail, setDetail] = useState<Detail | null>(null);
  const [checkpoints, setCheckpoints] = useState<Checkpoint[]>([]);
  const [history, setHistory] = useState<AuditEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [hireOpen, setHireOpen] = useState(false);
  const [closeOpen, setCloseOpen] = useState(false);
  const [closeOutcome, setCloseOutcome] = useState<CandidateOutcome>('unresponsive');
  const [closeReason, setCloseReason] = useState('');
  const [uploadFor, setUploadFor] = useState<Checkpoint | null>(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [celebrate, setCelebrate] = useState<{ x: number; y: number } | null | false>(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [removeFor, setRemoveFor] = useState<Checkpoint | null>(null);
  const [messageOpen, setMessageOpen] = useState(false);
  const [interviewOpen, setInterviewOpen] = useState(false);
  /** The company's default day rate, shown as the Day rate placeholder. */
  const [defaultRate, setDefaultRate] = useState<number | null>(null);
  /** Who can meet them on day one. One BC is no choice, so no picker. */
  const [trainers, setTrainers] = useState<Array<{ accountId: string; name: string }>>([]);
  /**
   * The messages that belong beside a checklist row, keyed by that row.
   * Fetched with the page rather than when the composer opens, because
   * the checklist offers them inline and a button that appears a second
   * late is a button nobody trusts.
   */
  const [stepMessages, setStepMessages] = useState<Record<string, { label: string; body: string }>>({});
  /**
   * The file being looked at. It is rendered INSIDE the app rather than
   * handed to a new tab: the stream endpoint needs the bearer token, so
   * the bytes have to be fetched, and a script-opened tab pointed at a
   * blob: URL is blocked or blank in several browsers — which reads, from
   * the outside, as "files still don't open".
   */
  const [viewing, setViewing] = useState<
    { label: string; url: string | null; type: string; error: string | null } | null
  >(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const openFile = useCallback(async (attachmentId: string, label: string) => {
    setViewing({ label, url: null, type: '', error: null });
    try {
      const blob = await HiringService.attachmentBlob(attachmentId);
      setViewing({
        label, url: URL.createObjectURL(blob), type: blob.type || '', error: null,
      });
    } catch (err: any) {
      // Say WHAT went wrong. A silent failure here is indistinguishable
      // from a file that was never stored.
      const status = err?.response?.status;
      setViewing({
        label,
        url: null,
        type: '',
        error: status === 404
          ? 'That file is no longer on record.'
          : status === 403
            ? 'Your role cannot open that file.'
            : 'That file could not be loaded. Try again in a moment.',
      });
    }
  }, []);

  // A blob URL is held by the document until it is released, so the tab
  // does not accumulate copies of every file that was looked at.
  const closeViewer = useCallback(() => {
    setViewing((current) => {
      if (current?.url) URL.revokeObjectURL(current.url);
      return null;
    });
  }, []);

  /**
   * Which message each step is offering.
   *
   * Refetched whenever the record changes, not once on arrival — the
   * whole point is that it MOVES: a call marked "No answer" offers the
   * message that rescues it, and the moment the interview is marked done
   * that same row offers the one that starts the background check. Asking
   * only at page load froze whichever answer was true when the page
   * opened.
   */
  const loadMessages = useCallback(async () => {
    if (!candidateId) return;
    try {
      const result = await HiringService.messages(candidateId);
      const byStep: Record<string, typeof result.messages> = {};
      result.messages.forEach((m) => {
        if (!m.about) return;
        (byStep[m.about] = byStep[m.about] || []).push(m);
      });

      const map: Record<string, { label: string; body: string }> = {};
      Object.entries(byStep).forEach(([step, candidates]) => {
        // The one that fits. Failing that, the LAST of them, because the
        // templates are in process order — once a step is behind you, the
        // useful thing to re-copy is the later message, not the one about
        // a call that did not connect three days ago.
        const pick = candidates.find((m) => m.fits) || candidates[candidates.length - 1];
        map[step] = { label: pick.label, body: pick.body };
      });
      setStepMessages(map);
    } catch {
      setStepMessages({});
    }
  }, [candidateId]);

  useEffect(() => { loadMessages(); }, [loadMessages]);

  useEffect(() => {
    HiringService.settings()
      .then((s) => { setDefaultRate(s.defaultDayRate); setTrainers(s.trainers || []); })
      .catch(() => setDefaultRate(null));
  }, []);

  const load = useCallback(async (quiet = false) => {
    if (!candidateId) return;
    try {
      const [d, c, h] = await Promise.all([
        HiringService.get(candidateId),
        HiringService.checkpoints(candidateId),
        HiringService.history(candidateId).catch(() => ({ items: [] as AuditEntry[], total: 0 })),
      ]);
      setDetail(d);
      setCheckpoints(c.checkpoints);
      setHistory(h.items);
      setError(null);
      // The record moved, so what each step is offering may have moved
      // with it — ticking the interview swaps its row's message.
      loadMessages();
    } catch (err: any) {
      if (!quiet) setError(err?.response?.data?.message || 'Could not open that candidate.');
    } finally {
      setLoading(false);
    }
  }, [candidateId, loadMessages]);

  useEffect(() => { load(); }, [load]);

  /**
   * Who comes before and after, in the order the list was in. The list is
   * the queue: a filter or a sort set on the table still governs who is
   * next here, because these are the same rows.
   */
  const { rows: pipeline } = usePipelineOrder();
  const neighbours = useMemo(() => {
    const index = pipeline.findIndex((row) => row._id === candidateId);
    return {
      previous: index > 0 ? pipeline[index - 1] : null,
      next: index >= 0 && index < pipeline.length - 1 ? pipeline[index + 1] : null,
      position: index >= 0 ? index + 1 : 0,
      total: pipeline.length,
    };
  }, [pipeline, candidateId]);

  const candidate = detail?.candidate;
  const closed = !!candidate && candidate.outcome !== 'open';
  // Hired is closed to the pipeline (no stage, no messages, no hire) but its
  // case stays editable: a detail fixed later, a step finished, a file that
  // came in after day one (which goes on to the driver record). Any other
  // closed case is frozen until it is reopened.
  const hired = candidate?.outcome === 'hired';
  const frozen = closed && !hired;
  const isBackOffice = canArea('hiringBackOffice');

  // Details are locked until somebody presses Edit. Every box saved on
  // blur, so a stray select-all-and-type or a slip of the delete key on
  // the way past wrote over a licence number with nothing to undo it.
  // Locked, a box is something to read and copy: a click puts its value
  // on the clipboard, which is most of what this card is used for.
  const [editing, setEditing] = useState(false);
  // Next/previous keeps this page mounted; the next candidate starts locked.
  useEffect(() => setEditing(false), [candidateId]);
  const locked = frozen || !editing;
  const lock = (label: string, value: string | number | null | undefined) => {
    if (!locked) return {};
    const text = value == null ? '' : String(value);
    return {
      // Reads as a value, not a box: no outline, a faint fill, and the
      // ordinary pointer — so the two states cannot be mistaken for each
      // other, and nothing about a locked field invites typing.
      InputProps: {
        readOnly: true,
        sx: {
          cursor: text ? 'pointer' : 'default',
          bgcolor: (t: Theme) => alpha(t.palette.text.primary, 0.03),
          transition: 'background-color 120ms',
          // An empty box has nothing to copy, so it does not light up.
          ...(text && { '&:hover': { bgcolor: (t: Theme) => alpha(t.palette.text.primary, 0.06) } }),
          // Light mode wants barely any; on a dark card the same 3% vanished.
          // A media query, not `palette.mode`: the theme switches scheme with
          // CSS variables, so in JS the mode always reads 'light'.
          '@media (prefers-color-scheme: dark)': {
            bgcolor: 'rgba(255, 255, 255, 0.08)',
            ...(text && { '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.13)' } }),
          },
          '& .MuiOutlinedInput-notchedOutline, &:hover .MuiOutlinedInput-notchedOutline, &.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: 'transparent',
          },
          '& input, & textarea, & .MuiSelect-select': { cursor: text ? 'pointer' : 'default' },
          '& .MuiSelect-icon': { display: 'none' },
          '& input::-webkit-calendar-picker-indicator': { display: 'none' },
        },
      },
      // Never focused: a focused box floats its label up into the outline
      // as though it were being typed in, which a locked one is not.
      onMouseDown: (e: React.MouseEvent) => e.preventDefault(),
      onClick: text ? async () => {
        try {
          await navigator.clipboard.writeText(text);
          setToast(`${label} copied`);
        } catch {
          setError(`${label} could not be copied.`);
        }
      } : undefined,
    };
  };

  // What each details group counts toward its tick. Only the fields this
  // viewer can see — the back-office ones are redacted for everybody
  // else, so counting them would leave those groups short for good.
  // Optional extras (who referred them, the SSN's on-file last four
  // standing in for a redacted full number) follow the same rule the
  // fields do.
  const contactProgress = countFilled(candidate
    ? [candidate.firstName, candidate.lastName, candidate.email, candidate.phone] : []);
  const driverProgress = countFilled(candidate ? [
    candidate.licenseNumber, candidate.licenseState, candidate.licenseExpiresAt,
    ...(isBackOffice ? [candidate.dateOfBirth, candidate.address] : []),
  ] : []);
  const employmentProgress = countFilled(candidate ? [
    ...(isBackOffice ? [
      candidate.ssn || candidate.ssnLast4,
      ...(candidate.usCitizen === false ? [candidate.uscisNumber] : []),
      candidate.everifyCase,
    ] : []),
    candidate.mecExpiresAt, candidate.fedexId, candidate.fedexPassword,
  ] : []);
  const onboardingProgress = countFilled(candidate ? [
    candidate.source,
    // Blank day rate means the company default, which is an answer.
    ...(isBackOffice ? [candidate.offeredPayRate ?? defaultRate] : []),
    candidate.uniformSizes, candidate.targetStartDate,
    // One trainer is chosen by default; none means there is nobody to ask for.
    ...(trainers.length > 1 ? [candidate.trainerAccountId] : []),
  ] : []);

  // "Hiring / All candidates / Matthew Ross". The page had no trail at all,
  // so the topbar said "Hiring" and there was nothing to click to get back
  // to the list you came from.
  usePageBreadcrumb(
    candidate
      ? [{ label: 'All candidates', to: '/hiring/list' }, { label: candidateName(candidate) }]
      : [{ label: 'All candidates', to: '/hiring/list' }],
  );

  const setField = async (patch: Record<string, any>) => {
    if (!candidateId) return;
    setSaving(true);
    try {
      await HiringService.update(candidateId, patch);
      await load(true);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'That change did not save.');
    } finally {
      setSaving(false);
    }
  };

  /**
   * Ticking a box used to set a page-wide `busy` flag that rendered a
   * LinearProgress above the content — so the whole page moved down four
   * pixels — and then awaited a full reload of the candidate, the
   * catalog and the history before the tick appeared. Three round trips
   * and a layout jump for a checkbox.
   *
   * Now the tick lands immediately, the request goes out behind it, and
   * only the stage line and the meter are refreshed from the response.
   * A failure puts the box back and says so.
   */
  const setCheckpoint = async (
    key: string,
    status: CheckpointStatus,
    opts: { value?: string; state?: string | null; at?: { x: number; y: number } } = {},
  ) => {
    if (!candidateId) return;
    const previous = checkpoints;
    const { value, state } = opts;

    setCheckpoints((current) => current.map((c) => {
      if (c.key !== key) return c;
      // Optimistic, including whose move it becomes — advancing a
      // background check from "link sent" to "they submitted it" hands it
      // from them to the lab, and the marker has to change with it.
      const nextOwner = status === 'in_progress' && state
        ? (c.states || []).find((st) => st.key === state)?.owedBy ?? c.owedByNow
        : status === 'not_started' ? c.owedBy ?? null
          : null;
      return {
        ...c,
        status,
        state: status === 'in_progress' ? state ?? null : null,
        owedByNow: nextOwner,
        value: value !== undefined ? value : c.value,
        completedAt: status === 'done' ? new Date().toISOString() : null,
        finished: status === 'done' || status === 'not_applicable',
      };
    }));

    // The one moment in the whole run worth marking: they are cleared to
    // drive. Everything else on these screens is deliberately quiet.
    if (key === 'fa_compliant' && status === 'done' && previous.find((c) => c.key === key)?.status !== 'done') {
      setCelebrate(opts.at || null);
    }

    try {
      const result = await HiringService.setCheckpoint(candidateId, key, { status, state, value });
      // The response IS the redrawn page. A tick moves more than its own
      // row — the stage, the meter, the next action and every deadline
      // behind it — and fetching the candidate, the checkpoints, the
      // history and the messages again to find that out is what made the
      // screen catch up a beat after the hand.
      if (result.detail && result.checkpoints) {
        setDetail(result.detail);
        // The MERGED list — definitions plus their rows. `detail.checkpoints`
        // is the raw stored rows, and rendering those drew a column of
        // ticks with no names under a heading reading UNKNOWN STAGE.
        setCheckpoints(result.checkpoints);
      } else {
        setDetail((current) => (current && result.stage !== current.candidate.stage
          ? { ...current, candidate: { ...current.candidate, stage: result.stage } }
          : current));
      }
      // Finishing something is activity, so who is stalled may have
      // changed — and the row's message may be a different message now.
      refreshStalled(true);
      loadMessages();
    } catch (err: any) {
      setCheckpoints(previous);
      setError(err?.response?.data?.message || 'That checkpoint did not change.');
    }
  };

  /**
   * File one document against a checkpoint.
   *
   * `expiresAt` is not optional decoration: a driver's licence and a
   * medical certificate state their own expiry, and the vault refuses to
   * file either without one (EXPIRY_REQUIRED). This page used to post the
   * file with no date, so those two uploads had never once succeeded —
   * which read as "HEIC does not upload", because a licence is exactly the
   * thing somebody photographs on a phone.
   */
  const fileCheckpoint = async (file: File, expiresAt: string | null) => {
    if (!candidateId) return;
    const form = new FormData();
    form.append('file', file);
    if (uploadFor?.documentTypeKey) form.append('documentTypeKey', uploadFor.documentTypeKey);
    if (uploadFor) form.append('label', checkpointLabel(uploadFor));
    if (expiresAt) form.append('expiresAt', expiresAt);

    setSaving(true);
    setUploadError(null);
    try {
      await HiringService.uploadAttachment(candidateId, form);
      // The expiry belongs on the candidate too — it is what the
      // compliance sweep watches once they are hired.
      if (expiresAt && uploadFor?.documentTypeKey === 'mec') {
        await HiringService.update(candidateId, { mecExpiresAt: expiresAt });
      }
      if (expiresAt && uploadFor?.documentTypeKey === 'driver_license') {
        await HiringService.update(candidateId, { licenseExpiresAt: expiresAt });
      }
      setToast('Document uploaded. Checkpoint marked complete.');
      setUploadOpen(false);
      setUploadFor(null);
      refreshStalled(true);
      await load(true);
    } catch (err: any) {
      setUploadError(err?.response?.data?.message || 'That file did not upload.');
    } finally {
      setSaving(false);
    }
  };

  const onFilePicked = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    fileCheckpoint(file, null);
  };

  const onHired = async (result: HireResult) => {
    setHireOpen(false);
    const training = result.training
      ? ` Training starts ${new Date(`${result.training.startedOn}T00:00:00Z`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })}; its daily steps are in Operations.`
      : '';
    setToast(
      (result.invitationSent
        ? `Hired. The invitation is on its way${result.promoted.length ? `, and ${result.promoted.length} document(s) moved to their driver record` : ''}.`
        : 'Hired, but the invitation did not go out. Resend it from Users.') + training,
    );
    refreshStalled(true);
    await load(true);
  };

  /**
   * The one line the page opens with: what is owed, and by whom.
   *
   * Everything in it was computable from the record already and none of it
   * was ever shown, so the page's answer to "what do I do for this person
   * today" was eighteen checkbox rows and a guess.
   */
  const next = useMemo(() => {
    if (!candidate || closed) return null;
    const progress = detail?.progress?.find((p) => p.stage === candidate.stage);
    const move = moveFor(candidate.stage, progress);
    // Everything still required ANYWHERE, not only in the stage they are
    // standing in — work skipped behind them is the case the old model
    // could not even represent, and it is the one worth naming first.
    const outstanding = (detail?.outstanding || progress?.outstanding || [])
      .map((key) => checkpoints.find((c) => c.key === key))
      .filter(Boolean)
      .map((c) => checkpointLabel(c as Checkpoint))
      .slice(0, 3);

    return {
      move,
      action: nextAction(candidate.stage),
      outstanding,
      // The only blocker worth stating before somebody presses Hire and
      // reads it in a dialog.
      blocker: !candidate.email ? 'No email on file, so they cannot be hired yet.' : null,
    };
  }, [candidate, closed, detail, checkpoints]);

  if (loading) {
    return (
      <PageBody>
        <HiringPanel />
        <LoadingState label="Opening…" />
      </PageBody>
    );
  }

  if (!candidate) {
    return (
      <PageBody>
        <HiringPanel />
        <Alert severity="error">{error || 'That candidate could not be found.'}</Alert>
      </PageBody>
    );
  }

  return (
    <PageBody>
      <HiringPanel />

      <PageHeader
        title={(
          <Stack direction="row" alignItems="center" flexWrap="wrap" columnGap={1.25} rowGap={0.5} sx={{ minWidth: 0 }}>
            {/* The same mark as the list, in the same colour, so the
                one place you form the judgement is also a place you can
                record it. Optimistic: it is a glance, not a form. */}
            <Tooltip title={candidate.starred ? 'Stop watching' : 'Worth watching'}>
              <IconButton
                size="small"
                data-testid="star-candidate"
                onClick={() => {
                  const next = !candidate.starred;
                  setDetail((current) => (current
                    ? { ...current, candidate: { ...current.candidate, starred: next } }
                    : current));
                  setField({ starred: next });
                }}
                sx={{ p: 0.25, flexShrink: 0, color: candidate.starred ? statusTokens.warn : 'text.disabled' }}
              >
                {candidate.starred
                  ? <StarIcon sx={{ fontSize: 22 }} />
                  : <StarBorderIcon sx={{ fontSize: 22 }} />}
              </IconButton>
            </Tooltip>
            <Box component="span" sx={{ minWidth: 0 }}>{candidateName(candidate)}</Box>
            {/* Straight to the next person, in the order the list was in.
                Working a pipeline is working a queue, and going back to
                the table between every candidate was the whole cost of
                it. Subtle on purpose: it is navigation, not an action. */}
            {neighbours.total > 1 && (
              <Stack direction="row" alignItems="center" gap={0.25} sx={{ flexShrink: 0 }}>
                <Tooltip title={neighbours.previous ? `Back to ${candidateName(neighbours.previous)}` : ''}>
                  <span>
                    <IconButton
                      size="small"
                      aria-label="Previous candidate"
                      disabled={!neighbours.previous}
                      onClick={() => neighbours.previous && navigate(`/hiring/${neighbours.previous._id}`)}
                      sx={{ color: 'text.disabled', '&:hover': { color: 'text.primary' } }}
                    >
                      <PrevIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title={neighbours.next ? `On to ${candidateName(neighbours.next)}` : ''}>
                  <span>
                    <IconButton
                      size="small"
                      aria-label="Next candidate"
                      disabled={!neighbours.next}
                      onClick={() => neighbours.next && navigate(`/hiring/${neighbours.next._id}`)}
                      sx={{ color: 'text.disabled', '&:hover': { color: 'text.primary' } }}
                    >
                      <NextIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                {neighbours.position > 0 && (
                  <Typography variant="body2" color="text.disabled" sx={{ ml: 0.5, fontSize: 13 }}>
                    {neighbours.position} of {neighbours.total}
                  </Typography>
                )}
              </Stack>
            )}
          </Stack>
        )}
        subtitle={
          closed
            ? `${OUTCOME_LABEL[candidate.outcome]}${candidate.outcomeReason ? ` — ${candidate.outcomeReason}` : ''}`
            : `${stageLabel(candidate.stage)} · ${detail?.daysSinceActivity === 0 ? 'touched today' : `${detail?.daysSinceActivity ?? 0} days since anybody touched this`}`
        }
        actions={
          <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
            <OutcomeChip candidate={candidate} />
            {candidate.outcome === 'hired' ? (
              <Button
                variant="outlined"
                onClick={() => navigate(`/files/entity/driver/${candidate.hiredAccountId}`)}
              >
                Open their driver file
              </Button>
            ) : closed ? (
              <Button
                variant="outlined" startIcon={<ReopenIcon />}
                onClick={async () => {
                  await HiringService.reopen(candidate._id);
                  refreshStalled(true);
                  load(true);
                }}
              >
                Reopen
              </Button>
            ) : (
              <>
                {/* The message is the work, most days. It sits with the
                    other two actions rather than inside a card further
                    down, because reaching out is what the first four
                    stages consist of. */}
                <Button
                  variant="outlined" startIcon={<MessageIcon />}
                  onClick={() => setMessageOpen(true)}
                >
                  Message
                </Button>
                {/* The call, with the script and the notes on one screen.
                    It sits beside Message because they are the same job
                    at two different points: reach them, then talk. */}
                <Button
                  variant="outlined" startIcon={<CallIcon />}
                  onClick={() => setInterviewOpen(true)}
                >
                  Interview
                </Button>
                <Button variant="outlined" startIcon={<CloseIcon />} onClick={() => setCloseOpen(true)}>
                  Close
                </Button>
                <Tooltip title={next?.blocker || ''}>
                  <span>
                    <Button
                      variant="contained"
                      startIcon={<HireIcon />}
                      disabled={!!next?.blocker}
                      onClick={() => setHireOpen(true)}
                    >
                      Hire
                    </Button>
                  </span>
                </Tooltip>
              </>
            )}
          </Stack>
        }
      />

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {/* ------------------------------------------------ what now */}
      {next && (
        <Card
          sx={{
            p: 2, mb: 3,
            borderLeft: '3px solid',
            borderLeftColor: next.blocker ? statusTokens.warn : moveColor(next.move),
          }}
        >
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            justifyContent="space-between"
            gap={1.5}
          >
            <Box sx={{ minWidth: 0 }}>
              <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 0.25 }}>
                <MoveDot move={next.move} />
                <Typography variant="subtitle2" color="text.primary">
                  {MOVE_LABEL[next.move]}
                </Typography>
              </Stack>
              <Typography variant="body2" color="text.secondary">
                {next.action}
                {next.outstanding.length > 0 && ` — ${next.outstanding.join(', ')}`}
                {next.blocker && ` · ${next.blocker}`}
              </Typography>

              {/* The actual next step, named, with its own clock on it.
                  The line above says what KIND of moment this is; this
                  says the one thing to do about it, and the same engine
                  writes it here and in the to-do list, so the page and
                  the list can never disagree. */}
              {detail?.nextAction && (() => {
                const tone = nextTone(detail.nextAction);
                const words = dueWords(detail.nextAction);
                const party = withWhom(detail.nextAction);
                // What the next action stepped past is on the checklist
                // rows themselves ("with First Advantage · 2 days late"),
                // not repeated here as a line of its own.
                return (
                  <>
                  <Stack direction="row" alignItems="center" gap={0.75} sx={{ mt: 0.5 }}>
                    <Box sx={{
                      width: 7, height: 7, borderRadius: '50%', flexShrink: 0, bgcolor: tone.color,
                    }}
                    />
                    <Typography variant="body2" color="text.primary">
                      {todoSentence(detail.nextAction)}
                    </Typography>
                    {!!words && (
                      <Typography
                        variant="caption"
                        sx={{ color: tone.late ? statusTokens.danger : 'text.secondary' }}
                      >
                        {words}
                        {party && ` · ${party}`}
                      </Typography>
                    )}
                  </Stack>
                  </>
                );
              })()}
            </Box>

            {/* The phone number was inert text in a disabled input, on the
                screen whose most-used piece of data it is. */}
            {/* Copy, not send. Outreach goes out from a phone and from
                whatever mail client is already open — what this screen is
                actually asked for is the string itself, and `tel:` /
                `mailto:` handed it to whatever the desktop had registered,
                which is usually nothing useful. The address is shown in
                full for the same reason: "Email" is not the thing you
                came for. */}
            <Stack direction="row" gap={1} flexWrap="wrap" sx={{ minWidth: 0, maxWidth: '100%' }}>
              {candidate.phone && (
                <CopyButton icon={<PhoneIcon />} value={candidate.phone} />
              )}
              {candidate.email && (
                <CopyButton icon={<MailIcon />} value={candidate.email} />
              )}
            </Stack>
          </Stack>
        </Card>
      )}

      {/* A grid, not two columns of stacks: the checklist and Details
          share a row, so the two cards end on the same line whatever
          their contents — the shorter simply gets the space at its
          foot. Files and History sit under Details. One column below
          `lg`, in the same order. */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'minmax(0, 1fr) 380px' },
          gap: 3,
        }}
      >
        {/* ------------------------------------------- left: the work */}
          <Card sx={{ p: 2.5, minWidth: 0 }}>
            <Typography variant="h5" sx={{ mb: 1.5 }}>Checklist</Typography>

            <CheckpointList
        nextActionKey={detail!.nextAction?.key || null}
              checkpoints={checkpoints}
              currentStage={candidate.stage}
              closed={frozen}
              blocked={detail?.blocked}
              // One value, two places to type it. The checklist row and
              // the Details box write the same candidate field, so they
              // cannot drift apart.
              fieldValues={{
                email: candidate.email,
                // The interview row's picker reads and writes this.
                scheduledCallAt: candidate.scheduledCallAt,
                ssnLast4: candidate.ssnLast4,
                uniformSizes: candidate.uniformSizes,
                targetStartDate: candidate.targetStartDate?.slice(0, 10),
              }}
              onSetField={(field, value) => setField({ [field]: value || null })}
              onRemoveFile={(checkpoint) => setRemoveFor(checkpoint)}
              onSet={setCheckpoint}
              onUpload={(checkpoint) => {
                setUploadFor(checkpoint);
                setUploadError(null);
                // Straight to the picker when nothing else is needed;
                // a dialog only where a date has to come with the file.
                if (needsExpiry(checkpoint)) setUploadOpen(true);
                else fileInput.current?.click();
              }}
              messages={stepMessages}
              onCopyMessage={async (message) => {
                try {
                  await navigator.clipboard.writeText(message.body);
                  setToast(`${message.label} copied`);
                } catch {
                  setError('That message could not be copied.');
                }
              }}
            />
          </Card>

        {/* --------------------------------- right: reference material */}
          <Card sx={{ p: 2.5, minWidth: 0 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
              <Box>
                <Typography variant="h5">Details</Typography>
                {locked && !frozen && (
                  <Typography variant="caption" color="text.secondary">Click a field to copy it</Typography>
                )}
                {hired && (
                  <Typography variant="caption" color="text.secondary" component="div">
                    Hired. This is their hiring case; editing it does not change their driver record.
                  </Typography>
                )}
              </Box>
              {!frozen && (
                <Button
                  size="small"
                  variant={editing ? 'contained' : 'outlined'}
                  startIcon={editing ? <DoneIcon /> : <EditIcon />}
                  onClick={() => setEditing((on) => !on)}
                >
                  {editing ? 'Done' : 'Edit'}
                </Button>
              )}
            </Stack>
            <Stack gap={2.5}>
              {/* Four groups, the old tracker's drawer shape — Contact,
                  Driver info, Employment, Notes — because five groups plus
                  a photo-reader button was "a bit too crowded", and the
                  reader itself never worked in the field. Back-office
                  fields sit in their natural groups and simply do not
                  render for a role the server redacts them from. */}
              <FieldGroup title="Contact" progress={contactProgress}>
                {/* The name was only ever typed once, in Add candidate —
                    a misspelling then went out in every message. */}
                <Stack direction="row" gap={2}>
                  <TextField
                    size="small" label="First name" defaultValue={candidate.firstName} fullWidth {...lock('First name', candidate.firstName)}
                    onBlur={(e) => {
                      const next = e.target.value.trim();
                      // A first name is the one thing a candidate cannot
                      // be without; clearing it puts the old one back.
                      if (!next) { e.target.value = candidate.firstName; return; }
                      if (next !== candidate.firstName) setField({ firstName: next });
                    }}
                  />
                  <TextField
                    size="small" label="Last name" defaultValue={candidate.lastName || ''} fullWidth {...lock('Last name', candidate.lastName)}
                    onBlur={(e) => e.target.value.trim() !== (candidate.lastName || '')
                      && setField({ lastName: e.target.value.trim() })}
                  />
                </Stack>
                <Stack direction="row" gap={2}>
                  <TextField
                    size="small" label="Email" defaultValue={candidate.email || ''} fullWidth {...lock('Email', candidate.email)}
                    onBlur={(e) => e.target.value !== (candidate.email || '') && setField({ email: e.target.value })}
                  />
                  <TextField
                    size="small" label="Mobile number" defaultValue={candidate.phone || ''} fullWidth {...lock('Mobile number', candidate.phone)}
                    onBlur={(e) => e.target.value !== (candidate.phone || '') && setField({ phone: e.target.value })}
                  />
                </Stack>
              </FieldGroup>

              <FieldGroup title="Driver info" progress={driverProgress}>
                <Stack direction="row" gap={2}>
                  <TextField
                    size="small" label="License number" fullWidth {...lock('License number', candidate.licenseNumber)}
                    defaultValue={candidate.licenseNumber || ''}
                    onBlur={(e) => e.target.value !== (candidate.licenseNumber || '') && setField({ licenseNumber: e.target.value })}
                  />
                  <TextField
                    size="small" label="State" sx={{ width: 110 }} {...lock('State', candidate.licenseState)}
                    defaultValue={candidate.licenseState || ''}
                    inputProps={{ maxLength: 2 }}
                    onBlur={(e) => e.target.value !== (candidate.licenseState || '') && setField({ licenseState: e.target.value })}
                  />
                </Stack>
                <Stack direction="row" gap={2}>
                  <TextField
                    size="small" label="License expires" type="date" fullWidth {...lock('License expires', candidate.licenseExpiresAt?.slice(0, 10))}
                    defaultValue={candidate.licenseExpiresAt?.slice(0, 10) || ''}
                    InputLabelProps={{ shrink: true }}
                    onBlur={(e) => e.target.value !== (candidate.licenseExpiresAt?.slice(0, 10) || '')
                      && setField({ licenseExpiresAt: e.target.value || null })}
                  />
                  {isBackOffice && (
                    <TextField
                      size="small" label="Date of birth" type="date" fullWidth {...lock('Date of birth', candidate.dateOfBirth?.slice(0, 10))}
                      defaultValue={candidate.dateOfBirth?.slice(0, 10) || ''}
                      InputLabelProps={{ shrink: true }}
                      onBlur={(e) => e.target.value !== (candidate.dateOfBirth?.slice(0, 10) || '')
                      && setField({ dateOfBirth: e.target.value || null })}
                    />
                  )}
                </Stack>
                {isBackOffice && (
                  <TextField
                    size="small" label="Address" fullWidth multiline minRows={2} {...lock('Address', candidate.address)}
                    defaultValue={candidate.address || ''}
                    onBlur={(e) => e.target.value !== (candidate.address || '') && setField({ address: e.target.value })}
                  />
                )}
              </FieldGroup>

              {/* Split by the pipeline, not by topic. Employment is what
                  Clearance runs on — every box here is mirrored by a
                  clearance checkpoint (paperwork, vetting, GroundCloud),
                  so filling the group is most of getting them cleared.
                  Onboarding is the rest of the hire in the order it
                  happens: how they arrived, the offer, kit, day one. */}
              <FieldGroup title="Employment" progress={employmentProgress}>
                {/* First: FedEx hands these out, and they are what the
                    rest of clearance is chased under. Both are typed here
                    and mirrored by their checkpoints; as checkpoint values
                    they disappeared the moment they were typed. */}
                <Stack direction="row" gap={2}>
                  <TextField
                    size="small" label="FedEx ID" fullWidth {...lock('FedEx ID', candidate.fedexId)}
                    defaultValue={candidate.fedexId || ''}
                    onBlur={(e) => e.target.value !== (candidate.fedexId || '') && setField({ fedexId: e.target.value })}
                  />
                  {/* The password FedEx gives out for this account. It
                      lives beside the ID because that is the pair
                      somebody reads out on day one, and the row in the
                      checklist ticks itself the moment it is here. */}
                  <TextField
                    size="small" label="FedEx password" fullWidth {...lock('FedEx password', candidate.fedexPassword)}
                    defaultValue={candidate.fedexPassword || ''}
                    onBlur={(e) => e.target.value !== (candidate.fedexPassword || '')
                      && setField({ fedexPassword: e.target.value })}
                  />
                </Stack>
                {isBackOffice && (
                  <Stack direction="row" gap={1.5} alignItems="center">
                    <TextField
                      size="small" label="SSN — last four" fullWidth {...lock('SSN — last four', candidate.ssn)}
                      defaultValue={candidate.ssn || ''}
                      placeholder="___-__-____"
                      inputProps={{ maxLength: 11 }}
                      // E-Verify wants the whole thing; the last four are
                      // derived from this on the server for everything
                      // that only confirms identity.
                      // The label keeps its old name by request; the box
                      // takes the whole number anyway, because E-Verify
                      // does.
                      helperText={!candidate.ssn && candidate.ssnLast4 ? `On file: ····${candidate.ssnLast4}` : undefined}
                      onBlur={(e) => e.target.value !== (candidate.ssn || '') && setField({ ssn: e.target.value })}
                    />
                    {/* Decides whether the USCIS number is on their
                        checklist at all. Not the same question as work
                        authorisation — a permanent resident is authorised
                        and still has one. */}
                    <FormControlLabel
                      sx={{ mr: 0, whiteSpace: 'nowrap' }}
                      control={(
                        <Checkbox
                          size="small"
                          // Ticked unless somebody says otherwise: on
                          // this workforce citizenship is the rule, and
                          // the box exists to mark the exception (which
                          // is what puts the USCIS number on their page).
                          checked={candidate.usCitizen !== false}
                          disabled={locked}
                          onChange={(e) => setField({ usCitizen: e.target.checked })}
                        />
                      )}
                      label={<Typography variant="body2">US citizen</Typography>}
                    />
                  </Stack>
                )}
                {/* Only when the box above says it is needed. The
                    checkpoint has the same rule (`onlyWhen`), so the
                    field and the step it ticks appear and disappear
                    together rather than one asking for something the
                    other says does not apply. */}
                {isBackOffice && candidate.usCitizen === false && (
                  <TextField
                    size="small" label="USCIS / A-number" fullWidth {...lock('USCIS / A-number', candidate.uscisNumber)}
                    defaultValue={candidate.uscisNumber || ''}
                    onBlur={(e) => e.target.value !== (candidate.uscisNumber || '')
                      && setField({ uscisNumber: e.target.value })}
                  />
                )}
                {/* E-Verify runs on the SSN above; the medical card is
                    the clinic half of vetting. */}
                <Stack direction="row" gap={2}>
                  {isBackOffice && (
                    <TextField
                      size="small" label="E-Verify case #" fullWidth {...lock('E-Verify case #', candidate.everifyCase)}
                      defaultValue={candidate.everifyCase || ''}
                      onBlur={(e) => e.target.value !== (candidate.everifyCase || '') && setField({ everifyCase: e.target.value })}
                    />
                  )}
                  <TextField
                    size="small" label="Medical cert. expires" type="date" fullWidth {...lock('Medical cert. expires', candidate.mecExpiresAt?.slice(0, 10))}
                    defaultValue={candidate.mecExpiresAt?.slice(0, 10) || ''}
                    InputLabelProps={{ shrink: true }}
                    onBlur={(e) => e.target.value !== (candidate.mecExpiresAt?.slice(0, 10) || '')
                      && setField({ mecExpiresAt: e.target.value || null })}
                  />
                </Stack>
              </FieldGroup>

              <FieldGroup title="Onboarding" progress={onboardingProgress}>
                {/* Same pair as Add candidate: the channel, and who or
                    which posting. It was read-only, so a wrong pick at
                    creation skewed the source numbers in Insights for good. */}
                <Stack direction="row" gap={2}>
                  <TextField
                    select size="small" label="Came from" sx={{ width: 150, flexShrink: 0 }} {...lock('Came from', SOURCE_LABEL[(candidate.source || 'other') as CandidateSource])}
                    value={candidate.source || 'other'}
                    onChange={(e) => e.target.value !== candidate.source && setField({ source: e.target.value })}
                  >
                    {(Object.keys(SOURCE_LABEL) as CandidateSource[]).map((s) => (
                      <MenuItem key={s} value={s}>{SOURCE_LABEL[s]}</MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    size="small" label="Who or which posting" fullWidth {...lock('Who or which posting', candidate.sourceDetail)}
                    defaultValue={candidate.sourceDetail || ''}
                    onBlur={(e) => e.target.value.trim() !== (candidate.sourceDetail || '')
                      && setField({ sourceDetail: e.target.value.trim() })}
                  />
                </Stack>
                {/* The number they said yes to, and what to order them. */}
                <Stack direction="row" gap={2}>
                  {/* Blank means the company default, which the message
                      quotes — so the placeholder shows what will actually
                      be said rather than leaving it to be guessed. This
                      box is for a rate offered to THIS person. */}
                  {isBackOffice && (
                    <TextField
                      size="small" label="Day rate" type="number" sx={{ width: 110, flexShrink: 0 }} {...lock('Day rate', candidate.offeredPayRate)}
                      placeholder={defaultRate !== null ? String(defaultRate) : undefined}
                      InputLabelProps={{ shrink: true }}
                      defaultValue={candidate.offeredPayRate ?? ''}
                      onBlur={(e) => {
                        const next = e.target.value === '' ? undefined : Number(e.target.value);
                        if (next !== candidate.offeredPayRate) setField({ offeredPayRate: next });
                      }}
                    />
                  )}
                  <TextField
                    size="small" label="Uniform sizes" fullWidth {...lock('Uniform sizes', candidate.uniformSizes)}
                    defaultValue={candidate.uniformSizes || ''}
                    onBlur={(e) => e.target.value !== (candidate.uniformSizes || '') && setField({ uniformSizes: e.target.value })}
                  />
                </Stack>
                {/* Day one: when, and who meets them. */}
                <Stack direction="row" gap={2}>
                  <TextField
                    size="small" label="Start date" type="date" fullWidth {...lock('Start date', candidate.targetStartDate?.slice(0, 10))}
                    defaultValue={candidate.targetStartDate?.slice(0, 10) || ''}
                    InputLabelProps={{ shrink: true }}
                    onBlur={(e) => e.target.value !== (candidate.targetStartDate?.slice(0, 10) || '')
                      && setField({ targetStartDate: e.target.value || null })}
                  />
                  {/* The BC who meets them on day one — the manager
                      introduction names them. With one BC there is nobody
                      to choose between, so it just says who. */}
                  {trainers.length > 1 ? (
                    <TextField
                      select size="small" label="Trainer" fullWidth {...lock('Trainer', trainers.find((t) => t.accountId === candidate.trainerAccountId)?.name)}
                      value={candidate.trainerAccountId || ''}
                      onChange={async (e) => {
                        await setField({ trainerAccountId: e.target.value || null });
                        loadMessages();
                      }}
                    >
                      <MenuItem value=""><em>Not chosen</em></MenuItem>
                      {trainers.map((t) => (
                        <MenuItem key={t.accountId} value={t.accountId}>{t.name}</MenuItem>
                      ))}
                    </TextField>
                  ) : trainers.length === 1 ? (
                    <TextField size="small" label="Trainer" fullWidth disabled value={trainers[0].name} />
                  ) : null}
                </Stack>
              </FieldGroup>

              {/* One box, so no "0 / 1" while it is empty — just the tick
                  and the wash once something is written. */}
              <FieldGroup title="Notes" progress={candidate.notes?.trim() ? [1, 1] : undefined}>
                <TextField
                  size="small" fullWidth multiline minRows={3} {...lock('Notes', candidate.notes)}
                  defaultValue={candidate.notes || ''}
                  onBlur={(e) => e.target.value !== (candidate.notes || '') && setField({ notes: e.target.value })}
                />
              </FieldGroup>
            </Stack>
          </Card>

        <Stack gap={3} sx={{ gridColumn: { lg: 2 }, minWidth: 0 }}>
          <Card sx={{ p: 2.5 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography variant="h5">Files</Typography>
              <Button
                size="small" startIcon={<UploadIcon />} disabled={frozen || saving}
                onClick={() => { setUploadFor(null); setUploadError(null); fileInput.current?.click(); }}
              >
                Add a file
              </Button>
            </Stack>

            {detail!.attachments.length === 0 ? (
              <Typography variant="body2" color="text.disabled">Nothing on file yet.</Typography>
            ) : (
              detail!.attachments.map((attachment) => (
                <Stack
                  key={attachment._id}
                  direction="row" alignItems="center" gap={1.5}
                  sx={{ py: 1, borderTop: '1px solid', borderColor: 'divider' }}
                >
                  <DocIcon sx={{ color: 'text.disabled' }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" noWrap>{attachment.label || 'File'}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatDate(attachment.uploadedAt)}
                      {attachment.expiresAt ? ` · expires ${formatDate(attachment.expiresAt)}` : ''}
                      {attachment.promotedDocumentId ? ' · filed to their driver record' : ''}
                    </Typography>
                  </Box>
                  <Tooltip title="Open this file">
                    <IconButton
                      size="small"
                      onClick={() => openFile(attachment._id, attachment.label || 'File')}
                    >
                      <OpenIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {!attachment.promotedDocumentId && !frozen && (
                    <IconButton
                      size="small"
                      onClick={async () => {
                        await HiringService.deleteAttachment(attachment._id);
                        load(true);
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                </Stack>
              ))
            )}
          </Card>

          {/* Last, on purpose. It is a record to consult when somebody
              asks what happened, not part of working the candidate — and
              it grows forever, so anywhere higher pushes the things that
              ARE the job further down the page. */}
          <Card sx={{ p: 2.5 }}>
            <Stack
              direction="row" alignItems="center" gap={1}
              onClick={() => setHistoryOpen((v) => !v)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setHistoryOpen((v) => !v); }
              }}
              sx={{ cursor: 'pointer', mb: historyOpen ? 2 : 0, borderRadius: 1 }}
            >
              <Typography variant="h5">History</Typography>
              <Typography variant="caption" color="text.disabled">
                {history.length || 'nothing yet'}
              </Typography>
              <Box sx={{ flex: 1 }} />
              <ExpandIcon
                sx={{
                  color: 'text.disabled',
                  transform: historyOpen ? 'none' : 'rotate(-90deg)',
                  transition: 'transform 150ms',
                }}
              />
            </Stack>

            {/* Shut by default. It is a record to consult, not a thing to
                read every time the page opens, and it grows forever. */}
            <Collapse in={historyOpen} unmountOnExit>
              {history.length === 0 ? (
                <Typography variant="body2" color="text.disabled">Nothing recorded yet.</Typography>
              ) : (
                <Stack gap={1.75}>
                  {history.map((entry) => (
                    <Box key={entry._id}>
                      <Typography variant="body2">{describe(entry)}</Typography>
                      <Typography variant="caption" color="text.disabled">
                        {entry.actorName || 'TopoSDS'} · {new Date(entry.at).toLocaleString()}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              )}
            </Collapse>
          </Card>
        </Stack>
      </Box>

      <input
        ref={fileInput}
        type="file"
        hidden
        accept=".pdf,.jpg,.jpeg,.png,.webp,.heic,.heif,image/heic,image/heif,application/pdf,image/*"
        onChange={onFilePicked}
      />

      {/* Unticking a filed document is a question about the FILE. Setting
          the row back to not-started on its own achieved nothing — the
          next sync saw the attachment and turned it green again, so the
          screen appeared to accept something it had not done. Making the
          deletion explicit is also the guard against an accidental
          untick. */}
      {candidate && (
        <InterviewMode
          open={interviewOpen}
          candidateId={candidate._id}
          candidateName={candidateName(candidate)}
          phone={candidate.phone}
          notes={candidate.notes || ''}
          covered={candidate.interviewCovered || []}
          onClose={() => setInterviewOpen(false)}
          /* Written to the record on screen at once, then persisted —
             and deliberately NOT through setField, which reloads the whole
             candidate. That reload is what pushed the server's copy of
             the notes back into the box somebody was still typing in. */
          onSaveNotes={async (notes) => {
            setDetail((current) => (current
              ? { ...current, candidate: { ...current.candidate, notes } }
              : current));
            try {
              await HiringService.update(candidateId!, { notes });
            } catch {
              setError('Those notes did not save.');
            }
          }}
          /* Applied to the record on screen at once, then persisted. A
             tick that waits for a round trip before it appears is a tick
             somebody presses twice, and re-fetching the whole candidate
             after each one is how the list came back empty. */
          onSaveCovered={async (interviewCovered) => {
            const before = detail?.candidate.interviewCovered || [];
            setDetail((current) => (current
              ? { ...current, candidate: { ...current.candidate, interviewCovered } }
              : current));
            try {
              await HiringService.update(candidateId!, { interviewCovered });
            } catch {
              setDetail((current) => (current
                ? { ...current, candidate: { ...current.candidate, interviewCovered: before } }
                : current));
              setError('That tick did not save.');
            }
          }}
        />
      )}

      {candidate && (
        <MessageComposer
          candidateId={candidate._id}
          candidateName={candidateName(candidate)}
          phone={candidate.phone}
          open={messageOpen}
          onClose={() => setMessageOpen(false)}
        />
      )}

      {/* The file itself, in the app. Images and PDFs render here; anything
          else is offered as a download, because a viewer that shows a
          spinner over a .docx is worse than a button that saves it. */}
      <Dialog open={!!viewing} onClose={closeViewer} maxWidth="md" fullWidth>
        <DialogTitle sx={{ pr: 6 }}>
          {candidate ? `${candidateName(candidate)} — ` : ''}{viewing?.label}
        </DialogTitle>
        <DialogContent
          dividers
          sx={{
            minHeight: 320, display: 'flex', alignItems: 'center', justifyContent: 'center',
            p: 0, bgcolor: 'action.hover',
          }}
        >
          {viewing?.error ? (
            <Alert severity="error" sx={{ m: 3 }}>{viewing.error}</Alert>
          ) : !viewing?.url ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 6 }}>Opening…</Typography>
          ) : viewing.type.startsWith('image/') ? (
            <Box
              component="img"
              src={viewing.url}
              alt={viewing.label}
              sx={{ maxWidth: '100%', maxHeight: '70vh', display: 'block' }}
            />
          ) : viewing.type === 'application/pdf' ? (
            <Box
              component="iframe"
              title={viewing.label}
              src={viewing.url}
              sx={{ width: '100%', height: '70vh', border: 0, bgcolor: 'background.paper' }}
            />
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ py: 6 }}>
              This kind of file opens outside the app — save it to look at it.
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          {viewing?.url && (
            <Button
              component="a"
              href={viewing.url}
              download={`${candidate ? `${candidateName(candidate)} - ` : ''}${viewing.label}`}
              startIcon={<DownloadIcon />}
            >
              Save a copy
            </Button>
          )}
          <Box sx={{ flex: 1 }} />
          <Button onClick={closeViewer}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!removeFor} onClose={() => setRemoveFor(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Remove {removeFor ? checkpointLabel(removeFor) : 'this file'}?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            This step is complete because the file is on record. Deleting the file is what
            reopens it.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setRemoveFor(null)}>Keep it</Button>
          <Button
            variant="contained"
            color="error"
            disableElevation
            disabled={saving}
            onClick={async () => {
              const target = removeFor;
              if (!target) return;
              setSaving(true);
              try {
                const match = detail?.attachments.find(
                  (a) => a.documentTypeKey === target.documentTypeKey && !a.promotedDocumentId,
                );
                if (match) await HiringService.deleteAttachment(match._id);
                setRemoveFor(null);
                refreshStalled(true);
                await load(true);
              } catch (err: any) {
                setError(err?.response?.data?.message || 'That file could not be removed.');
              } finally {
                setSaving(false);
              }
            }}
          >
            Delete the file
          </Button>
        </DialogActions>
      </Dialog>

      <UploadCheckpointDialog
        open={uploadOpen}
        checkpoint={uploadFor}
        busy={saving}
        error={uploadError}
        onClose={() => { setUploadOpen(false); setUploadFor(null); }}
        onUpload={fileCheckpoint}
      />

      <HireDialog
        open={hireOpen}
        candidate={candidate}
        trainers={trainers}
        onClose={() => setHireOpen(false)}
        onHired={onHired}
      />

      <Dialog open={closeOpen} onClose={() => setCloseOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Close {candidateName(candidate)}</DialogTitle>
        <DialogContent>
          {/* The label's room above the first field is the theme's
              (MuiDialogContent), for every dialog. */}
          <TextField
            select fullWidth label="What happened" value={closeOutcome}
            onChange={(e) => setCloseOutcome(e.target.value as CandidateOutcome)}
            sx={{ mb: 2 }}
            // The hint belongs in the menu, where you are choosing. In the
            // closed field it is a second line of small print under an
            // answer already given.
            SelectProps={{
              renderValue: (value) => (
                CLOSE_OUTCOMES.find((o) => o.value === value)?.label || String(value)
              ),
            }}
          >
            {CLOSE_OUTCOMES.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                <Box>
                  <Typography variant="body2">{o.label}</Typography>
                  <Typography variant="caption" color="text.secondary">{o.hint}</Typography>
                </Box>
              </MenuItem>
            ))}
          </TextField>
          <TextField
            fullWidth multiline minRows={2} label="Anything worth remembering"
            value={closeReason} onChange={(e) => setCloseReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setCloseOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={async () => {
              await HiringService.close(candidate._id, closeOutcome, closeReason.trim() || undefined);
              setCloseOpen(false);
              setCloseReason('');
              refreshStalled(true);
              load(true);
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {celebrate !== false && (
        <Confetti origin={celebrate} onDone={() => setCelebrate(false)} />
      )}

      <Snackbar
        open={!!toast}
        autoHideDuration={6000}
        onClose={() => setToast(null)}
        message={toast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </PageBody>
  );
};

// The audit trail in the product's own words. An entry that reads
// "candidate.checkpoint_set" is a log line, not history somebody can use.
function describe(entry: AuditEntry): string {
  const meta = entry.metadata || {};
  switch (entry.action) {
    case 'candidate.created':
      return 'Added to the pipeline';
    case 'candidate.stage_changed':
      return `Moved from ${label(meta.from)} to ${label(meta.to)}${meta.reason ? ` — ${meta.reason}` : ''}`;
    case 'candidate.checkpoint_set': {
      const name = meta.checkpoint
        ? (checkpointLabel({ key: meta.checkpoint, label: meta.label || meta.checkpoint }))
        : (meta.label || 'A step');
      return meta.to === 'done'
        ? `${name} completed${meta.by === 'document' ? ' by the file arriving' : ''}`
        : `${name} set to ${String(meta.to || '').replace(/_/g, ' ')}`;
    }
    case 'candidate.updated':
      return `Edited ${(meta.fields || []).join(', ')}`;
    case 'candidate.file_added':
      return 'A file was added';
    case 'candidate.file_removed':
      return 'A file was removed';
    case 'candidate.files_promoted':
      return `${(meta.promoted || []).length} document(s) filed to their driver record`;
    case 'candidate.closed':
      return `Closed as ${String(meta.outcome || '').replace(/_/g, ' ')}${meta.reason ? ` — ${meta.reason}` : ''}`;
    case 'candidate.reopened':
      return 'Reopened';
    case 'candidate.hired':
      return 'Hired — account created and invitation sent';
    case 'candidate.linked':
      return 'Linked to an existing driver record';
    case 'candidate.stalled':
      // Whose silence it was. At `lead` nobody had written to them yet,
      // so "gone quiet" would be blaming somebody for not answering a
      // message that was never sent.
      return meta.stage === 'lead'
        ? `Flagged after ${meta.idleDays} days with no outreach`
        : `Flagged as gone quiet after ${meta.idleDays} days`;
    case 'candidate.file_viewed':
      return 'A file was opened';
    case 'candidate.message_sent':
      // Named, because "which message went out and when" is the question
      // the history gets asked when somebody says they never heard back.
      return `Sent the ${String(meta.label || 'message').toLowerCase()} message`;
    default:
      return entry.action.replace('candidate.', '').replace(/_/g, ' ');
  }
}

function label(stage?: string): string {
  return stage && STAGE_LABEL[stage as CandidateStage]
    ? STAGE_LABEL[stage as CandidateStage].toLowerCase()
    : (stage || 'unknown');
}

export default CandidateDetailPage;
